<template>
  <div class="modal-card">
    <header class="modal-card-head">
      <div class="title-container">
        <b-icon
          icon="clock"
          :type="isContrast ? 'is-white-dark' : 'is-warning'"
          class="mr-5"
        />
        <span class="is-size-6 has-text-weight-semibold has-text-color-7">{{
          General.TEXTS.SCHEDULER_MODAL_TITLE
        }}</span>
      </div>
      <div
        v-if="$parent.canCancel.length > 0"
        class="close-button mt-10"
        @click="close()"
      >
        <b-icon icon="close" />
      </div>
    </header>

    <section class="modal-card-body spacing-p-10 px-35">
      <div
        class="is-flex is-justify-content-space-between is-align-items-center mb-10"
      >
        <span class="has-text-color-7 is-size-6">
          <span class="has-text-weight-semibold ">{{ General.DUE }}:</span>
          {{ dateRange.end | dateFormat("DD/MM/YYYY") }}
          {{ `( ${getDateDiff} ${General.DAYS} )` }}
        </span>
        <b-switch
          v-model="isWeekdays"
          class="has-text-color-7 spacing-my-10 is-size-7"
          type="is-success"
          size="is-small"
          @input="addEndDate(selectDefaultDate)"
        >
          {{ General.TEXTS.WEEKDAYS }}
        </b-switch>
      </div>
      <div class="title-container is-justify-content-space-between box">
        <DatePicker
          color="green"
          mode="date"
          :value="dateRange"
          :minDate="minDate"
          isRange
          :locale="locationConfig"
          :attributes="getAttributes"
          :selectAttribute="getSelectAttributes"
          :isDark="isContrast"
          @input="selectDates"
        />
        <div class="buttons-container">
          <b-button
            class="spacing-my-5 is-size-6"
            :type="selectDefaultDate === 10 ? 'is-primary' : ''"
            size="is-small"
            @click="addEndDate(10)"
          >
            <span>10 {{ General.DAYS.toLowerCase() }}</span>
          </b-button>
          <b-button
            class="spacing-my-5 is-size-6"
            :type="selectDefaultDate === 15 ? 'is-primary' : ''"
            size="is-small"
            @click="addEndDate(15)"
          >
            <span>15 {{ General.DAYS.toLowerCase() }}</span>
          </b-button>
          <b-button
            class="spacing-my-5 is-size-6"
            :type="selectDefaultDate === 20 ? 'is-primary' : ''"
            size="is-small"
            @click="addEndDate(20)"
          >
            <span>20 {{ General.DAYS.toLowerCase() }}</span>
          </b-button>
          <b-button
            class="spacing-my-5 is-size-6"
            :type="selectDefaultDate === 30 ? 'is-primary' : ''"
            size="is-small"
            @click="addEndDate(30)"
          >
            <span>30 {{ General.DAYS.toLowerCase() }}</span>
          </b-button>
          <b-button
            class="spacing-my-5 is-size-6"
            :type="selectDefaultDate === 60 ? 'is-primary' : ''"
            size="is-small"
            @click="addEndDate(60)"
          >
            <span>60 {{ General.DAYS.toLowerCase() }}</span>
          </b-button>
          <b-button
            class="spacing-my-5 is-size-6"
            :type="selectDefaultDate === 90 ? 'is-primary' : ''"
            size="is-small"
            @click="addEndDate(90)"
          >
            <span>90 {{ General.DAYS.toLowerCase() }}</span>
          </b-button>
        </div>
      </div>
    </section>

    <footer class="modal-card-foot is-justify-content-flex-end">
      <div class="buttons">
        <b-button
          :type="isContrast ? 'is-white-dark' : 'is-primary'"
          @click.stop="confirmAction()"
        >
          {{ General.SAVE.toUpperCase() }}
        </b-button>
      </div>
    </footer>
  </div>
</template>

<script>
import DatePicker from "v-calendar/lib/components/date-picker.umd";

import { App, General } from "@/utils/constants";
import { mapGetters } from "vuex";
import { correctionFactoreDateEnd, getDateDiffDay } from "@/utils/timeUtility";

export default {
  name: "ModalScheduler",

  components: { DatePicker },

  props: {
    dates: {
      type: Object,
      default: () => ({ start: new Date(), end: new Date() })
    },
    minDate: {
      type: Date,
      default: null
    },
    attributes: {
      type: Array,
      default: null
    },
    selectAttributes: {
      type: Object,
      default: null
    },
    onSelect: {
      type: Function,
      default: () => null
    }
  },

  data() {
    return {
      App,
      General,
      dateRange: {},
      locationConfig: {
        id: "pt-br",
        firstDayOfWeek: 1,
        masks: { weekdays: "WWW" }
      },
      isWeekdays: true,
      selectDefaultDate: 20
    };
  },

  computed: {
    ...mapGetters({
      holidays: "getHolidays",
      isContrast: "isContrast"
    }),
    getDateDiff() {
      return getDateDiffDay(
        this.dateRange.start,
        this.dateRange.end,
        this.holidays,
        this.isWeekdays
      );
    },
    getAttributes() {
      return (
        this.attributes || [
          {
            key: "Today",
            dot: true,
            dates: new Date()
          },
          {
            key: "Weekends",
            dot: "red",
            dates: this.isWeekdays ? [{ weekdays: [1, 7] }] : []
          },
          {
            key: "Holidays",
            dot: "orange",
            dates: this.isWeekdays ? this.holidays : []
          }
        ]
      );
    },
    getSelectAttributes() {
      return (
        this.selectAttributes || {
          highlight: "green",
          excludeDates: this.isWeekdays
            ? [{ weekdays: [1, 7] }, ...this.holidays]
            : null
        }
      );
    }
  },

  beforeMount() {
    this.dateRange = this.dates;
    this.isWeekdays = Boolean(this.dates?.isWeekdays);
    this.selectDefaultDate = this.getDateDiff;
  },

  methods: {
    selectDates(value) {
      this.dateRange = value;
      this.selectDefaultDate = 0;
    },

    confirmAction() {
      this.onSelect({ ...this.dateRange, isWeekdays: this.isWeekdays });
      this.close();
    },

    addEndDate(days) {
      this.selectDefaultDate = days;
      let weekends = 0;
      if (this.isWeekdays) {
        switch (days) {
          case 10:
            weekends = 2;
            break;
          case 15:
            weekends = 3;
            break;
          case 20:
            weekends = 4;
            break;
          case 30:
            weekends = 6;
            break;
          case 60:
            weekends = 12;
            break;
          case 90:
            weekends = 18;
            break;
        }
      }
      const millisecondsPerDay = 86400 * 1000;
      const newDates = {
        start: new Date(),
        end: new Date(
          Date.now() +
            days * millisecondsPerDay +
            (this.isWeekdays ? weekends * 2 * millisecondsPerDay : 0)
        )
      };

      newDates.end = correctionFactoreDateEnd(
        newDates,
        days,
        this.holidays,
        this.isWeekdays
      );
      this.dateRange = newDates;
    },

    close() {
      this.$parent.close();
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/variables";

.modal-card {
  display: flex;
  justify-content: space-between;
  width: 100%;
  min-width: 450px;
  min-height: 300px;
  background-color: $color-1;
}
.modal-card-head {
  justify-content: space-around;
}
.modal-card-body {
  background-color: $color-1;
}
.title-container {
  display: flex;
  flex-direction: row;
  align-items: center;
}
.buttons-container {
  display: flex;
  flex-direction: column;
  align-self: flex-start;
  align-items: center;
  .button {
    font-weight: bold;
  }
}

.close-button {
  position: absolute;
  right: 10px;
  top: 10px;
  cursor: pointer;
  &:hover {
    opacity: 0.7;
  }
}

.button.spacing-my-5 {
  border: none;
}
.vc-container {
  border: none;
  border-right: 1px solid $border-color;
  border-radius: 0;
}
.box {
  background-color: $color-1;
  box-shadow: 0 0.5rem 1rem $dark-white-shadow;
}
</style>
